// src/pages/ProductDetail.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../services/supabase';
import {
  FiArrowLeft,
  FiMinus,
  FiPlus,
  FiShoppingCart,
  FiTruck,
  FiDollarSign,
  FiRefreshCw,
  FiShare2,
  FiStar,
  FiPackage,
  FiMessageSquare,
  FiChevronLeft,
  FiChevronRight,
  FiCheckCircle
} from 'react-icons/fi';
import { FaLeaf, FaHandSpock, FaAward, FaStar, FaStarHalfAlt } from 'react-icons/fa';
import styles from './ProductDetail.module.css';

// --- NEW: Import watermark utility ---
import { getWatermarkedImageUrl } from '../utils/watermark';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [productImages, setProductImages] = useState([]);
  const [watermarkedImages, setWatermarkedImages] = useState([]); // <-- NEW
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('Standard');
  const [selectedSizePrice, setSelectedSizePrice] = useState(0);
  const [addingToCart, setAddingToCart] = useState(false);
  const [user, setUser] = useState(null);
  const [showReviews, setShowReviews] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewMessage, setReviewMessage] = useState('');
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showAddToCartToast, setShowAddToCartToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [canWriteReview, setCanWriteReview] = useState(false);
  const [userReview, setUserReview] = useState(null);
  const [sale, setSale] = useState(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchProduct();
    getCurrentUser();
  }, [id]);

  useEffect(() => {
    if (product) {
      fetchReviews();
      if (user) {
        checkIfUserCanReview();
      }
    }
  }, [product, user]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const checkIfUserCanReview = async () => {
    if (!user) {
      setCanWriteReview(false);
      return;
    }

    const { data } = await supabase
      .from('orders')
      .select(`
        id,
        status,
        order_items!inner (product_id)
      `)
      .eq('user_id', user.id)
      .eq('status', 'delivered')
      .eq('order_items.product_id', id);

    if (data && data.length > 0) setCanWriteReview(true);
    else setCanWriteReview(false);

    const { data: existingReview } = await supabase
      .from('reviews')
      .select('*')
      .eq('product_id', id)
      .eq('user_id', user.id)
      .single();

    if (existingReview) {
      setUserReview(existingReview);
      setCanWriteReview(false);
    } else {
      setUserReview(null);
    }
  };

  const parseProductImages = (product) => {
    let images = [];
    if (product.images) {
      try {
        const parsed = typeof product.images === 'string' ? JSON.parse(product.images) : product.images;
        if (Array.isArray(parsed) && parsed.length) images = parsed;
      } catch(e) {}
    }
    if (!images.length && product.image_url) images = [product.image_url];
    if (!images.length) images = ['/images/placeholder.jpg'];
    return images;
  };

  // --- NEW: Process images with watermark ---
  const processImages = async (images) => {
  const watermarked = await Promise.all(
    images.map(async (url) => {
      try {
        // Use the logo overlay
        return await getWatermarkedImageUrl(
          url,
          '/logo.png', // or '/watermark-logo.png' – place your logo image in public/
          {
            logoWidth: 180,
            position: 'bottom-right',
            padding: 20,
            opacity: 0.85,
            backgroundColor: 'rgba(0,0,0,0.3)',
          }
        );
      } catch {
        return url; // fallback
      }
    })
  );
  setWatermarkedImages(watermarked);
};

  const fetchActiveSaleForProduct = async (productId) => {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('sale_products')
      .select(`
        sale:sales (
          discount_type,
          discount_value,
          start_date,
          end_date,
          is_active
        )
      `)
      .eq('product_id', productId)
      .eq('sale.is_active', true)
      .lte('sale.start_date', now)
      .gte('sale.end_date', now)
      .maybeSingle();

    if (error || !data || !data.sale) return null;
    return data.sale;
  };

  const fetchProduct = async () => {
    setLoading(true);

    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .single();

    if (data && !error) {
      setProduct(data);
      const images = parseProductImages(data);
      setProductImages(images);
      // --- NEW: process images ---
      processImages(images);

      const saleData = await fetchActiveSaleForProduct(data.id);
      setSale(saleData);

      let relatedQuery = supabase
        .from('products')
        .select('*')
        .neq('id', id)
        .limit(6);
      if (data.category) relatedQuery = relatedQuery.eq('category', data.category);
      const { data: related } = await relatedQuery;
      if (related) setRelatedProducts(related);

      let productSizes = [];
      if (data.has_sizes && data.sizes) {
        try { productSizes = typeof data.sizes === 'string' ? JSON.parse(data.sizes) : data.sizes; } catch(e) {}
      }
      if (productSizes.length > 0) {
        setSelectedSize(productSizes[0].size);
        setSelectedSizePrice(productSizes[0].price);
      } else {
        setSelectedSize('Standard');
        setSelectedSizePrice(data.price);
      }
    }
    setLoading(false);
  };

  const fetchReviews = async () => {
    const { data } = await supabase
      .from('reviews')
      .select(`
        *,
        users:user_id (name, email)
      `)
      .eq('product_id', id)
      .order('created_at', { ascending: false });
    if (data) setReviews(data);
  };

  const handleSubmitReview = async () => {
    if (!user) {
      setReviewMessage('Please login to submit a review');
      setTimeout(() => setReviewMessage(''), 3000);
      navigate('/login');
      return;
    }
    if (!reviewText.trim()) {
      setReviewMessage('Please write your review');
      setTimeout(() => setReviewMessage(''), 3000);
      return;
    }

    setSubmittingReview(true);
    setReviewMessage('');

    const { error } = await supabase
      .from('reviews')
      .insert({
        product_id: id,
        user_id: user.id,
        rating: reviewRating,
        comment: reviewText
      });

    if (error) {
      setReviewMessage('Failed to submit review. Please try again.');
    } else {
      setReviewMessage('Review submitted successfully!');
      setReviewText('');
      setReviewRating(5);
      await fetchReviews();
      await checkIfUserCanReview();
      setShowReviews(true);
    }
    setTimeout(() => setReviewMessage(''), 3000);
    setSubmittingReview(false);
  };

  const getDiscountedPrice = () => {
    const basePrice = selectedSizePrice || product?.price || 0;
    if (!sale) return basePrice;
    if (sale.discount_type === 'percentage') {
      return basePrice * (1 - sale.discount_value / 100);
    } else {
      return Math.max(0, basePrice - sale.discount_value);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    setAddingToCart(true);

    const sizeToSave = product.has_sizes ? selectedSize : 'Standard';

    const { data: existingItem } = await supabase
      .from('cart_items')
      .select('*')
      .eq('user_id', user.id)
      .eq('product_id', product.id)
      .eq('size', sizeToSave)
      .single();

    if (existingItem) {
      await supabase
        .from('cart_items')
        .update({ quantity: existingItem.quantity + quantity })
        .eq('id', existingItem.id);
    } else {
      await supabase
        .from('cart_items')
        .insert({
          user_id: user.id,
          product_id: product.id,
          quantity: quantity,
          size: sizeToSave
        });
    }

    setAddingToCart(false);
    setToastMessage(`${product.name} (${sizeToSave}) added to cart!`);
    setShowAddToCartToast(true);
    setTimeout(() => setShowAddToCartToast(false), 3000);

    const event = new CustomEvent('itemAddedToCart', {
      detail: { productName: product.name, quantity }
    });
    window.dispatchEvent(event);

    const btn = document.querySelector(`.${styles.addToCartBtn}`);
    const mobileBtn = document.querySelector(`.${styles.mobileAddToCartBtn}`);
    const originalText = btn?.innerHTML || '';
    const mobileOriginal = mobileBtn?.innerHTML || '';
    if (btn) btn.innerHTML = '✓ Added to Cart!';
    if (mobileBtn) mobileBtn.innerHTML = '✓ Added!';
    setTimeout(() => {
      if (btn) btn.innerHTML = originalText;
      if (mobileBtn) mobileBtn.innerHTML = mobileOriginal;
    }, 2000);
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    if (navigator.share && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      try {
        await navigator.share({
          title: product?.name,
          text: product?.description?.substring(0, 100),
          url: shareUrl,
        });
      } catch (err) {
        copyToClipboard(shareUrl);
      }
    } else {
      copyToClipboard(shareUrl);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowShareTooltip(true);
      setTimeout(() => setShowShareTooltip(false), 2000);
    } catch (err) {
      fallbackCopyToClipboard(text);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
      setShowShareTooltip(true);
      setTimeout(() => setShowShareTooltip(false), 2000);
    } catch (err) {
      alert('Press Ctrl+C to copy the link: ' + text);
    }
    document.body.removeChild(textarea);
  };

  const nextImage = () => {
    if (productImages.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
    }
  };

  const prevImage = () => {
    if (productImages.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
    }
  };

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  const renderStars = (rating) => {
    const stars = [];
    const full = Math.floor(rating);
    const half = rating % 1 >= 0.5;
    for (let i = 1; i <= 5; i++) {
      if (i <= full) stars.push(<FaStar key={i} className={styles.starFilled} />);
      else if (i === full + 1 && half) stars.push(<FaStarHalfAlt key={i} className={styles.starHalf} />);
      else stars.push(<FaStar key={i} className={styles.starEmpty} />);
    }
    return stars;
  };

  const getAvailableSizes = () => {
    if (product?.has_sizes && product?.sizes) {
      try { return typeof product.sizes === 'string' ? JSON.parse(product.sizes) : product.sizes; } catch(e) { return []; }
    }
    return [];
  };

  const availableSizes = getAvailableSizes();
  const currentPrice = getDiscountedPrice();
  const originalPrice = selectedSizePrice || product?.price || 0;
  const isOnSale = sale !== null && currentPrice < originalPrice;

  const ReviewFormComponent = useMemo(() => {
    if (!showReviews || !canWriteReview || userReview) return null;
    return (
      <div className={isMobile ? styles.mobileWriteReview : styles.writeReviewForm}>
        <h4>Write Your Review</h4>
        {reviewMessage && (
          <div className={`${styles.reviewMessage} ${reviewMessage.includes('success') ? styles.success : styles.error}`}>
            {reviewMessage}
          </div>
        )}
        <div className={styles.ratingInput}>
          <label>Rating</label>
          <div className={styles.ratingStars}>
            {[1,2,3,4,5].map(star => (
              <button key={star} type="button" onClick={() => setReviewRating(star)}>
                {star <= reviewRating ? <FaStar className={styles.activeStar} /> : <FaStar className={styles.inactiveStar} />}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.reviewInput}>
          <label>Your Review</label>
          <textarea rows="3" value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Share your experience..." className={styles.reviewTextarea} />
        </div>
        <button onClick={handleSubmitReview} disabled={submittingReview} className={styles.submitReviewBtn}>
          {submittingReview ? 'Submitting...' : 'Submit Review'}
        </button>
      </div>
    );
  }, [showReviews, canWriteReview, userReview, isMobile, reviewMessage, reviewRating, reviewText, submittingReview]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading product details...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className={styles.notFoundContainer}>
        <div className={styles.notFound}>
          <div className={styles.notFoundIcon}>🔍</div>
          <h2>Product Not Found</h2>
          <p>The product you're looking for doesn't exist or has been removed.</p>
          <button onClick={() => navigate('/products')} className={styles.backToShopBtn}>
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // MOBILE UI
  // ============================================
  if (isMobile) {
    return (
      <div className={styles.mobileProductDetail}>
        {showAddToCartToast && (
          <div className={styles.mobileAddToCartToast}>
            <FiCheckCircle />
            <span>{toastMessage}</span>
          </div>
        )}

        <div className={styles.mobileHeader}>
          <button onClick={() => navigate(-1)} className={styles.mobileBackBtn}>
            <FiArrowLeft />
          </button>
          <h1>Product Details</h1>
          <button className={styles.mobileShareBtn} onClick={handleShare}>
            <FiShare2 />
          </button>
        </div>

        <div className={styles.mobileImageGallery}>
          <div className={styles.mobileImageScroll}>
            {productImages.map((img, idx) => (
              <div
                key={idx}
                className={`${styles.mobileImageItem} ${currentImageIndex === idx ? styles.active : ''}`}
                onClick={() => setCurrentImageIndex(idx)}
              >
                <img
                  src={watermarkedImages.length > 0 ? watermarkedImages[idx] : img}
                  alt={`${product.name} ${idx+1}`}
                />
              </div>
            ))}
          </div>
          <div className={styles.mobileMainImage}>
            <img
              src={watermarkedImages.length > 0 ? watermarkedImages[currentImageIndex] : productImages[currentImageIndex]}
              alt={product.name}
              onContextMenu={(e) => e.preventDefault()}
            />
            {product.is_bulk_order && <span className={styles.mobileBulkBadge}>Bulk Order</span>}
            {isOnSale && <span className={styles.mobileSaleBadge}>Sale!</span>}
            <button className={styles.mobileShareIcon} onClick={handleShare}>
              <FiShare2 />
            </button>
          </div>
          {productImages.length > 1 && (
            <div className={styles.mobileImageIndicators}>
              {productImages.map((_, idx) => (
                <span
                  key={idx}
                  className={`${styles.mobileIndicator} ${currentImageIndex === idx ? styles.active : ''}`}
                  onClick={() => setCurrentImageIndex(idx)}
                />
              ))}
            </div>
          )}
        </div>

        <div className={styles.mobileProductInfo}>
          <h2>{product.name}</h2>
          <div className={styles.mobileRating}>
            <div className={styles.mobileStars}>{renderStars(calculateAverageRating())}</div>
            <span>({reviews.length} reviews)</span>
          </div>
          <div className={styles.mobilePrice}>
            {isOnSale ? (
              <>
                <span className={styles.mobileOriginalPrice}>₹{originalPrice}</span>
                <span className={styles.mobileDealPrice}>₹{Math.round(currentPrice)}</span>
              </>
            ) : (
              <span>₹{currentPrice}</span>
            )}
          </div>
          <div className={styles.mobileStock}>
            {product.stock_quantity > 0 ? (
              <span className={styles.inStock}>✓ In Stock</span>
            ) : (
              <span className={styles.outOfStock}>✗ Out of Stock</span>
            )}
          </div>
          <div className={styles.mobileDescription}>
            <p>{product.description || 'Beautiful handcrafted toy with natural colors.'}</p>
          </div>

          {availableSizes.length > 0 && (
            <div className={styles.mobileSizeSection}>
              <label>Select Size</label>
              <div className={styles.mobileSizeOptions}>
                {availableSizes.map(size => (
                  <button
                    key={size.size}
                    className={`${styles.mobileSizeBtn} ${selectedSize === size.size ? styles.active : ''}`}
                    onClick={() => {
                      setSelectedSize(size.size);
                      setSelectedSizePrice(size.price);
                    }}
                  >
                    {size.size} <span className={styles.mobileSizePrice}>₹{size.price}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className={styles.mobileQuantitySection}>
            <label>Quantity</label>
            <div className={styles.mobileQuantityControl}>
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>
                <FiMinus />
              </button>
              <span>{quantity}</span>
              <button onClick={() => setQuantity(quantity + 1)} disabled={product.stock_quantity <= quantity}>
                <FiPlus />
              </button>
            </div>
          </div>

          <button
            className={styles.mobileAddToCartBtn}
            onClick={handleAddToCart}
            disabled={addingToCart || product.stock_quantity === 0}
          >
            <FiShoppingCart />
            {product.stock_quantity === 0 ? 'Out of Stock' : addingToCart ? 'Adding...' : `Add to Cart • ₹${Math.round(currentPrice * quantity)}`}
          </button>

          <div className={styles.mobileDeliveryInfo}>
            <div className={styles.mobileDeliveryItem}><FiTruck /> Free Shipping on orders above ₹499</div>
            <div className={styles.mobileDeliveryItem}><FiDollarSign /> Cash on Delivery available</div>
            <div className={styles.mobileDeliveryItem}><FiRefreshCw /> 7 days easy returns</div>
          </div>
        </div>

        {/* Reviews */}
        <div className={styles.mobileReviews}>
          <div className={styles.mobileReviewsHeader}>
            <h3>Customer Reviews</h3>
            {canWriteReview && !userReview && (
              <button onClick={() => setShowReviews(!showReviews)}>
                {showReviews ? 'Cancel' : 'Write a Review'}
              </button>
            )}
            {userReview && <span className={styles.reviewedBadge}>✓ You reviewed this</span>}
          </div>
          {ReviewFormComponent}
          <div className={styles.mobileReviewsList}>
            {reviews.length > 0 ? (
              reviews.map(review => (
                <div key={review.id} className={styles.mobileReviewCard}>
                  <div className={styles.mobileReviewHeader}>
                    <div className={styles.mobileReviewer}>
                      <div className={styles.mobileReviewerAvatar}>
                        {review.users?.name ? review.users.name[0].toUpperCase() : 'U'}
                      </div>
                      <div>
                        <div className={styles.mobileReviewerName}>{review.users?.name || 'Anonymous'}</div>
                        <div className={styles.mobileReviewStars}>{renderStars(review.rating)}</div>
                      </div>
                    </div>
                    <span className={styles.mobileReviewDate}>{new Date(review.created_at).toLocaleDateString()}</span>
                  </div>
                  <p>{review.comment}</p>
                  {review.user_id === user?.id && <span className={styles.yourReviewBadge}>Your Review</span>}
                </div>
              ))
            ) : (
              <p className={styles.mobileNoReviews}>No reviews yet. Be the first to review!</p>
            )}
          </div>
        </div>

        {relatedProducts.length > 0 && (
          <div className={styles.mobileRelated}>
            <div className={styles.mobileRelatedHeader}>
              <h3>You May Also Like</h3>
            </div>
            <div className={styles.mobileRelatedScroll}>
              {relatedProducts.map(rp => (
                <Link to={`/product/${rp.id}`} key={rp.id} className={styles.mobileRelatedCard}>
                  <div className={styles.mobileRelatedImage}>
                    <img src={parseProductImages(rp)[0] || '/images/placeholder.jpg'} alt={rp.name} />
                  </div>
                  <div className={styles.mobileRelatedInfo}>
                    <h4>{rp.name}</h4>
                    <p>₹{rp.price}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============================================
  // DESKTOP UI
  // ============================================
  return (
    <div className={styles.productDetailPage}>
      <div className={styles.container}>
        {showAddToCartToast && (
          <div className={styles.addToCartToast}>
            <FiCheckCircle /> {toastMessage}
          </div>
        )}

        <div className={styles.breadcrumb}>
          <Link to="/">Home</Link> <span>/</span> <Link to="/products">Products</Link> <span>/</span>
          <span className={styles.current}>{product.name}</span>
        </div>

        <div className={styles.productLayout}>
          <div className={styles.productGallery}>
            <div className={styles.mainImage}>
              <img
                src={watermarkedImages.length > 0 ? watermarkedImages[currentImageIndex] : productImages[currentImageIndex]}
                alt={product.name}
                onContextMenu={(e) => e.preventDefault()}
              />
              {product.is_bulk_order && <span className={styles.bulkBadge}>Bulk Order</span>}
              {isOnSale && <span className={styles.saleBadgeDesktop}>Sale!</span>}
              <div className={styles.shareContainer}>
                <button className={styles.shareBtn} onClick={handleShare}><FiShare2 /></button>
                {showShareTooltip && <span className={styles.shareTooltip}>Link copied!</span>}
              </div>
              {productImages.length > 1 && (
                <>
                  <button className={styles.navBtnPrev} onClick={prevImage}><FiChevronLeft /></button>
                  <button className={styles.navBtnNext} onClick={nextImage}><FiChevronRight /></button>
                </>
              )}
            </div>
            {productImages.length > 1 && (
              <div className={styles.thumbnailList}>
                {productImages.map((img, idx) => (
                  <div key={idx} className={`${styles.thumbnail} ${currentImageIndex === idx ? styles.active : ''}`} onClick={() => setCurrentImageIndex(idx)}>
                    <img
                      src={watermarkedImages.length > 0 ? watermarkedImages[idx] : img}
                      alt={`${product.name} view ${idx+1}`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.productInfo}>
            <h1 className={styles.productName}>{product.name}</h1>
            <div className={styles.ratingSection}>
              <div className={styles.stars}>{renderStars(calculateAverageRating())}</div>
              <span className={styles.reviewCount}>({reviews.length} reviews)</span>
            </div>
            <div className={styles.priceSection}>
              {isOnSale ? (
                <>
                  <span className={styles.originalPriceDesktop}>₹{originalPrice}</span>
                  <span className={styles.dealPriceDesktop}>₹{Math.round(currentPrice)}</span>
                </>
              ) : (
                <span className={styles.currentPrice}>₹{currentPrice}</span>
              )}
            </div>
            <div className={styles.stockInfo}>
              {product.stock_quantity > 10 ? (
                <span className={styles.inStock}>✓ In Stock</span>
              ) : product.stock_quantity > 0 ? (
                <span className={styles.lowStock}>⚠ Only {product.stock_quantity} left</span>
              ) : (
                <span className={styles.outOfStock}>✗ Out of Stock</span>
              )}
            </div>
            <div className={styles.description}>
              <p>{product.description || 'Beautiful handcrafted toy with natural colors.'}</p>
            </div>

            {availableSizes.length > 0 && (
              <div className={styles.sizeSection}>
                <div className={styles.sizeHeader}>Select Size</div>
                <div className={styles.sizeOptions}>
                  {availableSizes.map(size => (
                    <button
                      key={size.size}
                      className={`${styles.sizeBtn} ${selectedSize === size.size ? styles.active : ''}`}
                      onClick={() => {
                        setSelectedSize(size.size);
                        setSelectedSizePrice(size.price);
                      }}
                    >
                      {size.size} <span className={styles.sizePrice}>₹{size.price}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.quantitySection}>
              <div className={styles.quantityHeader}>Quantity</div>
              <div className={styles.quantityControl}>
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} disabled={quantity <= 1}>
                  <FiMinus />
                </button>
                <span>{quantity}</span>
                <button onClick={() => setQuantity(Math.min(product.stock_quantity, quantity + 1))} disabled={product.stock_quantity <= quantity}>
                  <FiPlus />
                </button>
              </div>
            </div>

            <button
              className={styles.addToCartBtn}
              onClick={handleAddToCart}
              disabled={addingToCart || product.stock_quantity === 0}
            >
              <FiShoppingCart />
              {product.stock_quantity === 0 ? 'Out of Stock' : addingToCart ? 'Adding...' : `Add to Cart - ₹${Math.round(currentPrice * quantity)}`}
            </button>

            <div className={styles.deliveryInfo}>
              <div className={styles.deliveryItem}><FiTruck /><div><strong>Free Shipping</strong><span>on orders above ₹499</span></div></div>
              <div className={styles.deliveryItem}><FiDollarSign /><div><strong>Cash on Delivery</strong><span>pay when you receive</span></div></div>
              <div className={styles.deliveryItem}><FiRefreshCw /><div><strong>Easy Returns</strong><span>7 days policy</span></div></div>
            </div>
            <div className={styles.features}>
              <span><FaLeaf /> Eco-Friendly</span>
              <span><FaHandSpock /> Handcrafted</span>
              <span><FaAward /> GI Tagged</span>
              <span><FiPackage /> Safe Packaging</span>
            </div>
          </div>
        </div>

        {/* Reviews Desktop */}
        <div className={styles.reviewsSection}>
          <div className={styles.reviewsHeader}>
            <div className={styles.reviewsTitle}><FiMessageSquare /><h3>Customer Reviews</h3></div>
            <div className={styles.reviewsStats}>
              <div className={styles.avgRating}>
                <span className={styles.avgNumber}>{calculateAverageRating()}</span>
                <div className={styles.avgStars}>{renderStars(calculateAverageRating())}</div>
                <span>{reviews.length} reviews</span>
              </div>
              {canWriteReview && !userReview && (
                <button className={styles.writeReviewBtn} onClick={() => setShowReviews(!showReviews)}>
                  {showReviews ? 'Cancel' : 'Write Review'}
                </button>
              )}
              {userReview && <span className={styles.reviewedBadgeDesktop}>✓ You have reviewed this product</span>}
            </div>
          </div>
          {ReviewFormComponent}
          <div className={styles.reviewsList}>
            {reviews.length > 0 ? (
              reviews.map(review => (
                <div key={review.id} className={styles.reviewCard}>
                  <div className={styles.reviewHeader}>
                    <div className={styles.reviewerInfo}>
                      <div className={styles.reviewerAvatar}>{review.users?.name ? review.users.name[0].toUpperCase() : 'U'}</div>
                      <div>
                        <span className={styles.reviewerName}>{review.users?.name || 'Anonymous'}</span>
                        <div className={styles.reviewStars}>{renderStars(review.rating)}</div>
                      </div>
                    </div>
                    <span className={styles.reviewDate}>{new Date(review.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className={styles.reviewComment}>{review.comment}</p>
                  {review.user_id === user?.id && <span className={styles.yourReviewBadgeDesktop}>Your Review</span>}
                </div>
              ))
            ) : (
              <div className={styles.noReviews}><p>No reviews yet. Be the first to review!</p></div>
            )}
          </div>
        </div>

        <div className={styles.relatedScroll}>
          {relatedProducts.map(rp => (
            <div key={rp.id} className={styles.relatedCard}>
              <Link to={`/product/${rp.id}`}>
                <div className={styles.relatedImage}>
                  <img src={parseProductImages(rp)[0] || '/images/placeholder.jpg'} alt={rp.name} />
                </div>
                <div className={styles.relatedInfo}>
                  <h4>{rp.name}</h4>
                  <div className={styles.relatedPrice}>₹{rp.price}</div>
                  <button className={styles.relatedBtn}>View</button>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;